import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconRotateLeft } from '@sorare/core/src/atoms/icons/system/iconRotateLeft.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { CommonShards } from '@sorare/core/src/components/CommonShards';
import { CommonCardPrice } from '@sorare/core/src/components/collections/CommonCardPrice';
import { DropCardsWarningDialog } from '@sorare/core/src/components/commonGame/DropCardsWarningDialog';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import {
  cardAttributes,
  commonMarketplace,
  fantasy,
} from '@sorare/core/src/lib/glossary';

import { useIsCardDroppable } from '#src/hooks/useIsCardDroppable.ts';

import type { SellCommonCard_anyCard } from './__generated__/index.graphql.ts';

type Props = {
  card: SellCommonCard_anyCard;
  onSelect: () => void;
  selected: Nullable<boolean>;
};

const Root = styled(Vertical)`
  width: 100%;
`;
const StyledSpan = styled.span`
  font-weight: normal !important;
  color: var(--c-nd-700);
`;

export const SellCommonCard = ({ card, onSelect, selected }: Props) => {
  const { signedDropCardPrice } = card;
  const { sport } = useSportContext();
  const [showDropCardWarningDialog, setShowDropCardWarningDialog] =
    useState(false);

  const isCardDroppable = useIsCardDroppable();

  const { isDroppable, isOwner, isUsedInLineup } = isCardDroppable(card);

  const buttonLabelNode = useMemo(() => {
    if (!isOwner) return <FormattedMessage defaultMessage="Card dropped" />;

    if (!signedDropCardPrice)
      return <FormattedMessage {...commonMarketplace.dropNotAvailable} />;
    return <FormattedMessage {...commonMarketplace.drop} />;
  }, [isOwner, signedDropCardPrice]);

  if (sport !== Sport.BASEBALL && card.signedDropCardPrice) {
    return (
      <>
        <Button
          fullWidth
          size="small"
          color="quaternary"
          onClick={onSelect}
          startIcon={
            selected ? (
              <FontAwesomeIcon icon={iconRotateLeft} />
            ) : (
              <CommonShards cardSet={card.cardSet} size={2} />
            )
          }
          disableDebounce
        >
          {selected ? (
            <Horizontal gap={0.5}>
              {' '}
              <FormattedMessage
                {...fantasy.undoNumber}
                values={{
                  value: card.signedDropCardPrice.price,
                  span: chunks => <StyledSpan>{chunks}</StyledSpan>,
                }}
              />
            </Horizontal>
          ) : (
            <FormattedNumber value={card.signedDropCardPrice.price} />
          )}
        </Button>
        {!!isUsedInLineup && (
          <Button fullWidth size="small" color="quaternary" disabled>
            <LabelS bold>
              <FormattedMessage {...cardAttributes.usedInLineup} />
            </LabelS>
          </Button>
        )}
      </>
    );
  }

  return (
    <Root>
      <Horizontal>
        <CommonCardPrice price={signedDropCardPrice?.price} />
      </Horizontal>
      <Button
        fullWidth
        size="small"
        color="tertiary"
        onClick={() => {
          if (isUsedInLineup) {
            setShowDropCardWarningDialog(true);
          }
        }}
        disabled={!isDroppable}
      >
        {buttonLabelNode}
      </Button>
      {!!isUsedInLineup && (
        <Button fullWidth size="small" color="quaternary" disabled>
          <LabelS bold>
            <FormattedMessage {...cardAttributes.usedInLineup} />
          </LabelS>
        </Button>
      )}
      <DropCardsWarningDialog
        open={showDropCardWarningDialog}
        onCancel={() => setShowDropCardWarningDialog(false)}
        onContinue={() => {
          setShowDropCardWarningDialog(false);
        }}
        cards={[card]}
        loading={false}
      />
    </Root>
  );
};

SellCommonCard.fragments = {
  anyCard: gql`
    fragment SellCommonCard_anyCard on AnyCardInterface {
      slug
      sport
      signedDropCardPrice {
        price
        signedPrice
      }
      cardSet {
        slug
        branding {
          id
          colors {
            main
          }
        }
      }
      ...useIsCardDroppable_anyCard
      ...DropCardsWarningDialog_anyCardInterface
    }
    ${useIsCardDroppable.fragments.anyCard}
    ${DropCardsWarningDialog.fragments.anyCardInterface}
  ` as TypedDocumentNode<SellCommonCard_anyCard>,
};
